import React, { useState, useEffect, useImperativeHandle, forwardRef, useRef } from "react";
import { COOKIE_KEYS, USER_TYPES, API_DETAILS } from "../App";
import Cookies from "js-cookie";
import axios from "axios";
import "../styles/Youro-header.css";
import { useNavigate, useLocation, matchPath } from "react-router-dom";
import { FaBell } from "react-icons/fa";
import { useChatContext } from "../context/ChatContext";
import displayIcon from "../assets/Display_icon.png";

// Indexes into the patient profile's view switcher. Named so the provider
// branch in changeView can tell Help & Support apart from the rest.
const HELP_VIEW = 6;
// Billing is patient-only, so it never reaches the provider branch below.
const BILLING_VIEW = 8;

const Youroheader = forwardRef((props, ref) => {
  const navigate = useNavigate();
  const location = useLocation();
  const { stompObject, setStompObject, setIsLoggedIn } = useChatContext();
  const audioPlayer = useRef(null);

  const userName = Cookies.get(COOKIE_KEYS.userName) || "User";
  const userType = Cookies.get(COOKIE_KEYS.userType);

  const [imagePreview, setImagePreview] = useState(null);
  const [notifications, setNotifications] = useState([]);
  const [showNotificationPopup, setShowNotificationPopup] = useState(false);
  const [showProfile, setShowProfile] = useState(false);
  const [email, setEmail] = useState('');
  const closeProfile = () => setShowProfile(false);
  const openProfile = () => setShowProfile(true);

  /**
   * Reopens the menu for anyone arriving from a screen outside the patient
   * shell — the Privacy Policy is a route of its own, so it cannot call the
   * handle below. The state is cleared straight away so a refresh does not
   * spring the menu open again.
   */
  useEffect(() => {
    if (!location.state?.openProfileMenu) return;
    setShowProfile(true);
    navigate(location.pathname, { replace: true, state: null });
  }, [location.state?.openProfileMenu]);

  const handleLogout = () => {
    Cookies.remove(COOKIE_KEYS.userId);
    Cookies.remove(COOKIE_KEYS.token);
    Cookies.remove(COOKIE_KEYS.userType);

    if (stompObject) {
      stompObject.disconnect(() => console.log("Disconnected from WebSocket."));
      setStompObject(null);
      setIsLoggedIn(false);
    }
    navigate("/");
  };

  const changeView = (view) => {
    if (userType === USER_TYPES.doctor) {
      // Providers used to land on /doctor-profile whatever they clicked, so
      // Help & Support silently opened My Profile. Route the items a provider
      // actually has; the patient-only ones are not rendered for them at all.
      closeProfile();
      navigate(view === HELP_VIEW ? "/help-support" : "/doctor-profile");
      return;
    }
    if (userType === USER_TYPES.user) {
      props?.setView?.(view);
      closeProfile();
      return;
    }
  };

  const fetchNotifications = async () => {
    const userId = Cookies.get(COOKIE_KEYS.userId);
    if (!userId) return;

    const apiUrl =
      API_DETAILS.baseUrl +
      API_DETAILS.PORT +
      API_DETAILS.baseExtension +
      `/getNotifications/${userId}?timeZone=${Intl.DateTimeFormat().resolvedOptions().timeZone}`;

    try {
      const res = await axios.get(apiUrl);
      setNotifications(res.data || []);
    } catch (e) {
      console.error("Error fetching notifications:", e);
    }
  };

  const fetchProfileData = async () => {
    const uIdLocal = Cookies.get(COOKIE_KEYS.userId);
    const url = API_DETAILS.baseUrl + API_DETAILS.PORT + API_DETAILS.baseExtension + `/getUser/${uIdLocal}`;

    const config = {
      headers: {
        'Access-Control-Allow-Origin': '*',
        'Access-Control-Allow-Methods': '*',
        'Content-Type': 'application/json'
      }
    };

    try {
      const res = await axios.get(url, config);
      setEmail(res.data.email);
    } catch (err) {
      console.error(err);
    }
  };

  useImperativeHandle(ref, () => ({
    fetchNotifications,
    // Lets the screens opened from this menu put the patient back where they
    // started, instead of guessing at a view number or walking browser history.
    openProfile,
  }));

  const closeNotification = async (notId) => {
    const apiUrl =
      API_DETAILS.baseUrl +
      API_DETAILS.PORT +
      API_DETAILS.baseExtension +
      `/deleteNotifications/${notId}`;
    try {
      await axios.delete(apiUrl);
      fetchNotifications();
    } catch (e) {
      console.error("Error clearing notification:", e);
    }
  };

  const clearAllNotifications = async () => {
    const userId = Cookies.get(COOKIE_KEYS.userId);
    const apiUrl =
      API_DETAILS.baseUrl +
      API_DETAILS.PORT +
      API_DETAILS.baseExtension +
      `/deleteAllNotifications/${userId}`;
    try {
      await axios.delete(apiUrl);
      fetchNotifications();
    } catch (e) {
      console.error("Error clearing all notifications:", e);
    }
  };

  const getProfilePic = async () => {
    const userId = Cookies.get(COOKIE_KEYS.userId);
    if (!userId) return;

    const url =
      API_DETAILS.baseUrl +
      API_DETAILS.PORT +
      API_DETAILS.baseExtension +
      `/getDp/${userId}`;

    try {
      const response = await axios.get(url, { responseType: "arraybuffer" });
      if (response.status !== 200) return;

      const bytes = new Uint8Array(response.data);
      if (!bytes || bytes.length === 0) return;

      const base64Image = btoa(
        bytes.reduce((data, byte) => data + String.fromCharCode(byte), "")
      );
      setImagePreview(`data:image/jpeg;base64,${base64Image}`);
    } catch (e) {
      console.error("Error loading dp:", e);
    }
  };

  useEffect(() => {
    getProfilePic();
    fetchProfileData();
    fetchNotifications();
    const id = setInterval(fetchNotifications, 500 * 1000);
    return () => clearInterval(id);
  }, []);

  // ===== Notification Popup (matches your current logic, just styled clean) =====
  const NotificationPopup = () => {
    return (
      <div className="yh-notif">
        <div className="yh-notif-head">
          <div className="yh-notif-title">Notifications</div>
          <div className="yh-notif-actions">
            {notifications.length > 0 && (
              <button className="yh-notif-clear" onClick={clearAllNotifications}>
                Clear All
              </button>
            )}
            <button
              className="yh-notif-close"
              onClick={() => setShowNotificationPopup(false)}
              aria-label="Close"
            >
              ×
            </button>
          </div>
        </div>

        <div className="yh-notif-body">
          {notifications.length === 0 ? (
            <div className="yh-notif-empty">No new notifications</div>
          ) : (
            notifications.map((n) => (
              <div key={n.notId} className="yh-notif-item">
                <div className="yh-notif-msg">{n.message}</div>
                <div className="yh-notif-meta">
                  {n.date
                    ? new Date(n.date.replace(" at ", " ")).toLocaleString("en-US", {
                        weekday: "short",
                        month: "short",
                        day: "numeric",
                        year: "numeric",
                        hour: "numeric",
                        minute: "2-digit",
                        timeZoneName: "short"
                      })
                    : ""
                  }
                </div>
                <button
                  className="yh-notif-item-close"
                  onClick={() => closeNotification(n.notId)}
                >
                  close
                </button>
              </div>
            ))
          )}
        </div>
      </div>
    );
  };

  const MenuButton = ({ icon, label, onClick }) => {
    const [isHovered, setIsHovered] = useState(false);

    const baseStyle = {
      width: '100%',
      padding: '16px',
      borderRadius: '8px',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'space-between',
      cursor: 'pointer',
      textAlign: 'left',
      // Default Styles
      border: '1px solid #e5e7eb',
      backgroundColor: 'transparent',
      color: '#333',
      transform: 'scale(1)',
      transition: 'all 0.3s ease-in-out',
    };

    const hoverStyle = {
      border: '2px solid #ff6f61',
      backgroundColor: 'rgba(255, 111, 97, 0.05)',
      color: '#ff6f61',
      transform: 'scale(1.05)',
    };

    const currentStyle = isHovered ? { ...baseStyle, ...hoverStyle } : baseStyle;
    const iconColor = isHovered ? '#ff6f61' : 'var(--youro-sage, #9CB189)';
    const arrowOpacity = isHovered ? 1 : 0.5;
    const arrowColor = isHovered ? '#ff6f61' : 'currentColor';

    return (
      <button
        onClick={onClick}
        style={currentStyle}
        onMouseEnter={() => setIsHovered(true)}
        onMouseLeave={() => setIsHovered(false)}
      >
        <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
          {React.cloneElement(icon, { style: { width: '20px', height: '20px', color: iconColor, transition: 'color 0.2s' } })}
          <span style={{ fontWeight: '500', color: isHovered ? '#ff6f61' : 'var(--youro-charcoal, #333)' }}>{label}</span>
        </div>
        <svg
          xmlns="http://www.w3.org/2000/svg"
          width="24"
          height="24"
          viewBox="0 0 24 24"
          fill="none"
          stroke={arrowColor}
          strokeWidth="2"
          strokeLinecap="round"
          strokeLinejoin="round"
          style={{ width: '20px', height: '20px', opacity: arrowOpacity, transition: 'all 0.2s' }}
        >
          <path d="m9 18 6-6-6-6"></path>
        </svg>
      </button>
    );
  };
  const ROUTE_TITLES = [
    // Public
    { path: "/", title: "Home" },
    { path: "/conditions", title: "Conditions" },
    { path: "/conditions/:id", title: "Condition Details" },
    { path: "/assessment/:name?", title: "Assessment" },
    { path: "/login", title: "Login" },
    { path: "/forgot-password", title: "Forgot Password" },
    { path: "/verify-email", title: "Verify Email" },
    { path: "/signup", title: "Sign Up" },
    { path: "/signupmyself", title: "Sign Up" },
    { path: "/signupforfamilymember", title: "Sign Up" },
    { path: "/signupprovider", title: "Provider Sign Up" },
    { path: "/adminsignup", title: "Admin Sign Up" },
    { path: "/policy", title: "Privacy Policy" },
    { path: "/telehealth-consent", title: "Telehealth Consent" },
    { path: "/terms-conditions", title: "Terms & Conditions" },
    { path: "/about", title: "About Us" },
    { path: "/pricing", title: "Pricing" },

    // Patient
    { path: "/patient-home", title: "Dashboard" },

    // Doctor
    { path: "/doctor-home", title: "Dashboard" },
    { path: "/doctor-profile", title: "Profile" },
    { path: "/doctor-access", title: "Access Status" },
    { path: "/doctor-appointment", title: "Appointments" },
    { path: "/doctor-view-profile/:patientId?/:apptId?", title: "Patient Profile" },
    { path: "/doctor-chat", title: "Chat" },

    // Admin
    { path: "/admin-doctors", title: "Doctors" },
    { path: "/admin-patients", title: "Patients" },
    { path: "/admin-denied-doctors", title: "Denied Doctors" },
    { path: "/admin-view-doctor", title: "Doctor Profile" },
    { path: "/admin-view-patient", title: "Patient Profile" },
    { path: "/manage-approved-medicine", title: "Maintenance" },
    { path: "/admin-assistants", title: "Assistants" },
    { path: "/admin-note-template", title: "Note Templates" },
    { path: "/admin-add-template", title: "Add Template" },
  ];

  const getTitleFromPath = (pathname) => {
    // IMPORTANT: Put "/" last so it doesn't match everything
    const ordered = [...ROUTE_TITLES].sort((a, b) => (a.path === "/" ? 1 : -1));

    const match = ordered.find((r) =>
      matchPath({ path: r.path, end: true }, pathname)
    );

    return match?.title || "";
  };

  const computedTitle = props.pageTitle || getTitleFromPath(location.pathname);


  return (
    <>
      <header className="yh">
        {/* LEFT: Brand */}
        <div className="yh-left">
          <div className="yh-brand">{computedTitle}</div>
        </div>

        {/* RIGHT: Notifications + Profile */}
        <div className="yh-right">
          {/* Bell */}
          <button
            className="yh-bell"
            onClick={() => setShowNotificationPopup(true)}
            aria-label="Notifications"
          >
            <FaBell size={18} />
            {notifications.length > 0 && (
              <span className="yh-badge">{notifications.length}</span>
            )}
          </button>

          <div className="yh-divider" />

          {/* Profile */}
          <button className="yh-profile" onClick={() => setShowProfile(true)}>
            <img
              src={props.imagePreview || imagePreview || displayIcon}
              className="yh-avatar"
              alt="Profile"
            />
            <div className="yh-profile-text">
              <div className="yh-name">{userName}</div>
              <div className="yh-sub">View Profile</div>
            </div>
          </button>

          {/* If you want logout visible in header (optional) */}
          {/* <button className="yh-logout" onClick={handleLogout}>Logout</button> */}
        </div>
      </header>

      {/* Popup overlay */}
      {showNotificationPopup && (
        <div
          className="yh-overlay"
          onClick={() => setShowNotificationPopup(false)}
        >
          <div className="yh-modal" onClick={(e) => e.stopPropagation()}>
            <NotificationPopup />
          </div>
        </div>
      )}

      {/* 2. The Modal Overlay & Content */}
      {showProfile && (
        <div
          style={{
            position: 'fixed',
            top: 0,
            left: 0,
            width: '100vw',
            height: '100vh',
            backgroundColor: 'rgba(0, 0, 0, 0.5)',
            zIndex: 9999,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center'
          }}
          onClick={closeProfile}
        >
          <div
            onClick={(e) => e.stopPropagation()}
            style={{
              backgroundColor: '#fff',
              position: 'relative',
              width: '100%',
              maxWidth: '500px',
              borderRadius: '12px',
              boxShadow: '0 20px 25px -5px rgba(0, 0, 0, 0.1), 0 10px 10px -5px rgba(0, 0, 0, 0.04)',
              border: '1px solid #e5e7eb',
              pointerEvents: 'auto',
              overflow: 'hidden',
              margin: '20px',
              animation: 'fadeIn 0.2s ease-out',
              display: 'flex',
              flexDirection: 'column'
            }}
          >
            {/* Header (Visually Hidden) */}
            <div style={{ position: 'absolute', width: '1px', height: '1px', padding: 0, margin: '-1px', overflow: 'hidden', clip: 'rect(0, 0, 0, 0)', whiteSpace: 'nowrap', borderWidth: 0 }}>
              <h2>Profile Settings</h2>
            </div>

            {/* User Info Section */}
            <div style={{ padding: '24px', borderBottom: '1px solid #e5e7eb' }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: '16px' }}>
                <div
                  style={{
                    width: '80px', height: '80px', borderRadius: '50%', display: 'flex', alignItems: 'center', justifyContent: 'center',
                    backgroundColor: 'var(--youro-sage, #9CB189)', color: '#ffffff', fontSize: '1.5rem', fontWeight: 700
                  }}
                >
                  {userName ? userName.split(' ').map(n => n[0]).join('').substring(0, 2).toUpperCase() : 'AM'}
                </div>
                <div style={{ display: 'flex', flexDirection: 'column' }}>
                  <h3 style={{ margin: '0 0 4px 0', fontSize: '1.25rem', fontWeight: 600, color: '#000' }}>{userName || "Alex Martinez"}</h3>
                  <p style={{ margin: '0 0 4px 0', opacity: 0.7, fontSize: '1rem' }}>{email || "xxxxxxxxx@email.com"}</p>
                  <p style={{ margin: 0, fontSize: '0.875rem', opacity: 0.6 }}>Member since xxxxxxxx</p>
                </div>
              </div>
            </div>

            {/* Menu Options - Using the new MenuButton component */}
            <div style={{ padding: '16px', display: 'flex', flexDirection: 'column', gap: '8px' }}>

              <MenuButton
                label="Personal Information"
                icon={<svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M19 21v-2a4 4 0 0 0-4-4H9a4 4 0 0 0-4 4v2"></path><circle cx="12" cy="7" r="4"></circle></svg>}
                onClick={() => changeView(4)}
              />

              {/* Prescriptions, Medical Records and Billing describe a patient's
                  own records and mean nothing on a provider account, so they are
                  rendered for patients only. Providers reach prescriptions
                  through the sidebar, which is scoped to their caseload. */}
              {userType !== USER_TYPES.doctor && (
                <>
                  <MenuButton
                    label="Prescriptions"
                    icon={<svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="m10.5 20.5 10-10a4.95 4.95 0 1 0-7-7l-10 10a4.95 4.95 0 1 0 7 7Z"></path><path d="m8.5 8.5 7 7"></path></svg>}
                    onClick={() => changeView(5)}
                  />

                  <MenuButton
                    label="Medical Records"
                    icon={<svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M6 22a2 2 0 0 1-2-2V4a2 2 0 0 1 2-2h8a2.4 2.4 0 0 1 1.704.706l3.588 3.588A2.4 2.4 0 0 1 20 8v12a2 2 0 0 1-2 2z"></path><path d="M14 2v5a1 1 0 0 0 1 1h5"></path><path d="M10 9H8"></path><path d="M16 13H8"></path><path d="M16 17H8"></path></svg>}
                    onClick={() => changeView(7)}
                  />

                  <MenuButton
                    label="Billing"
                    icon={<svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><rect width="20" height="14" x="2" y="5" rx="2"></rect><line x1="2" x2="22" y1="10" y2="10"></line></svg>}
                    onClick={() => changeView(BILLING_VIEW)}
                  />
                </>
              )}

              <MenuButton
                label="Privacy & Security"
                icon={<svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><rect width="18" height="11" x="3" y="11" rx="2" ry="2"></rect><path d="M7 11V7a5 5 0 0 1 10 0v4"></path></svg>}
                onClick={() => {
                  closeProfile();
                  // The policy page is a public route, reachable from signup too,
                  // so it is told where it was opened from — that is what lets its
                  // back button return here rather than into browser history.
                  navigate('/policy', { state: { from: 'profile' } });
                }}
              />

              <MenuButton
                label="Help & Support"
                icon={<svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><circle cx="12" cy="12" r="10"></circle><path d="M9.09 9a3 3 0 0 1 5.83 1c0 2-3 3-3 3"></path><path d="M12 17h.01"></path></svg>}
                onClick={() => changeView(HELP_VIEW)}
              />

            </div>

            {/* Log Out */}
            <div style={{ padding: '0 16px 24px 16px' }}>
              <button
                // We can apply similar logic here inline since it's a single button
                onMouseEnter={(e) => {
                  e.currentTarget.style.transform = 'scale(1.05)';
                  e.currentTarget.style.backgroundColor = 'rgba(255, 111, 97, 0.05)';
                }}
                onMouseLeave={(e) => {
                  e.currentTarget.style.transform = 'scale(1)';
                  e.currentTarget.style.backgroundColor = 'transparent';
                }}
                onClick={handleLogout}
                style={{ width: '100%', padding: '14px', borderRadius: '8px', display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '8px', border: '2px solid var(--youro-coral, #f87171)', backgroundColor: 'transparent', color: 'var(--youro-coral, #f87171)', cursor: 'pointer', transition: 'all 0.2s' }}
              >
                <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" style={{ width: '20px', height: '20px' }}><path d="m16 17 5-5-5-5"></path><path d="M21 12H9"></path><path d="M9 21H5a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h4"></path></svg>
                <span style={{ fontWeight: 600 }}>Log Out</span>
              </button>
            </div>

            {/* Close X Button */}
            <button
              type="button"
              onClick={closeProfile}
              style={{ position: 'absolute', top: '16px', right: '16px', opacity: 0.7, cursor: 'pointer', border: 'none', background: 'transparent', padding: '4px' }}
            >
              <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" style={{ width: '24px', height: '24px' }}><path d="M18 6 6 18"></path><path d="m6 6 12 12"></path></svg>
            </button>
          </div>
        </div>
      )}
    </>
  );
});

export default Youroheader;
